import React, {useEffect, useState} from "react";
import {Alert, Box, Chip, CircularProgress, Link, Paper, Stack, Typography} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import ComposicaoDaFatura from "../../../shared/ui/ComposicaoDaFatura";
import {formatDataIsoBR} from "../../../shared/utils/formatters";
import {getBackendErrorMessage} from "../../../shared/utils/httpError";
import {getStatusColor} from "../../Licencas/licencasUtils";
import {getLicencasDaEmpresa, getPreviaDaEmpresa} from "../EmpresaService";
import {agendadosDaLicenca, getMeioLabel, rotuloDaLicenca, situacaoDaLicenca} from "../empresasUtils";

const lerLista = (response) => {
    const data = response?.data?.data ?? response?.data?.valor ?? response?.data;
    return Array.isArray(data) ? data : [];
};

const mensagemDaPrevia = (error) => (error?.response?.status === 502
    ? 'A prévia da fatura está indisponível no momento. Tente de novo em alguns minutos.'
    : getBackendErrorMessage(error, 'Não foi possível calcular a prévia da fatura.'));

function Cartao({titulo, subtitulo, children}) {
    return (
        <Paper variant="outlined" sx={{borderRadius: 2, p: {xs: 2, md: 2.5}}}>
            <Typography sx={{fontSize: '1.0625rem', fontWeight: 600}}>{titulo}</Typography>
            {subtitulo ? <Typography variant="body2" color="text.secondary" sx={{mt: 0.25}}>{subtitulo}</Typography> : null}
            <Box sx={{mt: 2}}>{children}</Box>
        </Paper>
    );
}

/*
 * Assinaturas da empresa e a próxima fatura, que junta todas elas (BBF-148). As duas vêm da API: a lista do
 * app-licencas e a prévia do faturamento, que usa o mesmo cálculo da emissão e não grava nada.
 */
export default function AssinaturasDaEmpresa({empresa}) {
    const empresaId = empresa?.id;
    const [licencas, setLicencas] = useState({carregando: true, itens: [], erro: ''});
    const [previa, setPrevia] = useState({carregando: true, valor: null, erro: ''});

    useEffect(() => {
        if (!empresaId) return undefined;
        let ativo = true;
        setLicencas({carregando: true, itens: [], erro: ''});
        setPrevia({carregando: true, valor: null, erro: ''});
        getLicencasDaEmpresa(empresaId)
            .then((response) => ativo && setLicencas({carregando: false, itens: lerLista(response), erro: ''}))
            .catch((error) => ativo && setLicencas({carregando: false, itens: [], erro: getBackendErrorMessage(error, 'Não foi possível carregar as assinaturas.')}));
        getPreviaDaEmpresa(empresaId)
            .then((response) => ativo && setPrevia({carregando: false, valor: response?.data?.data ?? null, erro: ''}))
            .catch((error) => ativo && setPrevia({carregando: false, valor: null, erro: mensagemDaPrevia(error)}));
        return () => {
            ativo = false;
        };
    }, [empresaId]);

    const porId = new Map(licencas.itens.map((licenca) => [licenca.id, licenca]));
    const tenantDe = (licencaId) => porId.get(licencaId)?.tenant_id;
    const rotuloDaSecao = (secao) => `${secao.plano} v${secao.versao}`;
    const detalheDaSecao = (secao) => {
        const licenca = porId.get(secao.licenca_id);
        return (
            <>
                {tenantDe(secao.licenca_id) ? <Chip size="small" variant="outlined" label={tenantDe(secao.licenca_id)} sx={{fontFamily: 'monospace'}} /> : null}
                {licenca?.meio_pagamento ? <Chip size="small" variant="outlined" label={getMeioLabel(licenca.meio_pagamento)} /> : null}
                <Chip size="small" variant="outlined" label={secao.modalidade === 'POS_PAGO' ? 'Pós-pago' : 'Pré-pago'} />
            </>
        );
    };
    const rotuloDeFora = (item) => {
        const licenca = porId.get(item.licenca_id);
        return licenca ? `${rotuloDaLicenca(licenca)} (${licenca.tenant_id})` : 'Assinatura';
    };

    return (
        <Stack spacing={3}>
            <Cartao titulo="Assinaturas" subtitulo="Cada assinatura é um tenant próprio. A forma de pagamento fica em cada uma.">
                {licencas.carregando ? <CircularProgress size={20} /> : null}
                {licencas.erro ? <Alert severity="warning">{licencas.erro}</Alert> : null}
                {!licencas.carregando && !licencas.erro && !licencas.itens.length ? (
                    <Typography variant="body2" color="text.secondary">Nenhuma assinatura ainda. As assinaturas são criadas em Licenças.</Typography>
                ) : null}
                {licencas.itens.length ? (
                    <Box sx={{overflowX: 'auto'}}>
                        <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', minWidth: 620, '& th, & td': {px: 1.5, py: 1.25, textAlign: 'left', borderTop: 1, borderColor: 'divider', fontSize: 14, verticalAlign: 'top'}, '& th': {color: 'text.secondary', fontWeight: 500, borderTop: 0}}}>
                            <thead>
                                <tr>
                                    <th>Assinatura</th>
                                    <th>Situação</th>
                                    <th>Meio</th>
                                    <th>Desde</th>
                                </tr>
                            </thead>
                            <tbody>
                                {licencas.itens.map((licenca) => (
                                    <tr key={licenca.id}>
                                        <td>
                                            <Link component={RouterLink} to={`/licencas/${licenca.id}`} variant="body2" fontWeight={600} underline="hover" display="block">{rotuloDaLicenca(licenca)}</Link>
                                            <Typography variant="caption" color="text.secondary" sx={{fontFamily: 'monospace'}}>{licenca.tenant_id}</Typography>
                                            {licenca.plano?.sistema?.nome ? <Typography variant="caption" color="text.secondary" display="block">{licenca.plano.sistema.nome}</Typography> : null}
                                        </td>
                                        <td>
                                            <Chip size="small" label={situacaoDaLicenca(licenca)} color={getStatusColor(licenca.status)} variant="outlined" />
                                            {agendadosDaLicenca(licenca).map((item) => (
                                                <Typography key={item} variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>{item}</Typography>
                                            ))}
                                        </td>
                                        <td>{getMeioLabel(licenca.meio_pagamento)}</td>
                                        <td>{formatDataIsoBR(licenca.validade_inicio)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Box>
                    </Box>
                ) : null}
            </Cartao>

            <Cartao
                titulo="Próxima fatura"
                subtitulo={previa.valor
                    ? `Prévia do fechamento de ${formatDataIsoBR(previa.valor.emissao)}, que vence em ${formatDataIsoBR(previa.valor.vencimento)}. Ciclo de ${formatDataIsoBR(previa.valor.ciclo_inicio)} a ${formatDataIsoBR(previa.valor.ciclo_fim)}. Mesmo cálculo da emissão.`
                    : 'Prévia do próximo fechamento, com todas as assinaturas da empresa. Mesmo cálculo da emissão.'}
            >
                {previa.carregando ? <CircularProgress size={20} /> : null}
                {previa.erro ? <Alert severity="warning">{previa.erro}</Alert> : null}
                {previa.valor ? (
                    <ComposicaoDaFatura previa={previa.valor} rotuloDaSecao={rotuloDaSecao} detalheDaSecao={detalheDaSecao} rotuloDeFora={rotuloDeFora} />
                ) : null}
            </Cartao>
        </Stack>
    );
}
